import type { Flag } from './flag-types';
import type { FlagManager } from './useFlag';
import { resolveObjectives } from 'helpers/objectives';
import { objectivesLocked } from 'helpers/objectives-identity';
import { toast } from '@/components/ui/sonner';
import { objectiveOptionGroups } from '../experiments/objective-options';
import { useWebsiteDetails } from '../useWebsiteDetails';

type FlagGoalCopy = { saved: string };

type UseFlagGoalParams = { manager: FlagManager; flag: Flag; copy: FlagGoalCopy };

/**
 * The flag's primary goal, the event the guardrail judges On and Off on (resolveObjectives, as
 * run-flag-guardrails.ts reads it). Chosen from the website's events, as an experiment's is in
 * GoalsConfig, and saved through PUT /experiments/:id. The API fixes objectives while the flag
 * is live (objectivesLocked), so the picker is read only then.
 */
export const useFlagGoal = ({ manager, flag, copy }: UseFlagGoalParams) => {
  const { eventGoals } = useWebsiteDetails(flag.websiteId ?? 0);

  const goals = (eventGoals ?? []).map(({ name, role }) => ({ name, role }));
  // What "Website default" would measure, named in the picker even while another event is chosen.
  const websiteDefault = resolveObjectives({ ...flag, primaryGoal: null });
  const groups = objectiveOptionGroups({ goals, selected: flag.primaryGoal ? [flag.primaryGoal] : [] });

  const isLive = objectivesLocked(flag.status);
  const isLocked = isLive || manager.isArchived || !manager.canEdit || manager.pending === 'goal';

  const save = async (next: string | null) => {
    if (isLocked) return;
    if (await manager.savePrimaryGoal(next)) toast.success(copy.saved);
  };

  return {
    value: flag.primaryGoal ?? null,
    defaultEvent: websiteDefault.primaryIsDefault ? null : websiteDefault.primary,
    groups,
    hasEvents: goals.length > 0,
    isLocked,
    // The reason the picker is read only, when it is the API's rule rather than the person's rights.
    isFixedLive: isLive,
    save,
  };
};

export type FlagGoalManager = ReturnType<typeof useFlagGoal>;
